import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const LineChart = ({ data, type, page }: any) => {
  const chartdata = data ? { ...data } : null;
  const isDataQuality = type === "dataQuality";
  const options = {
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    scales: {
      x: {
        grid: isDataQuality
          ? {
              color: "rgba(100, 116, 139, 0.12)",
            }
          : undefined,
        ticks: isDataQuality
          ? {
              color: "#334155",
            }
          : undefined,
        title: {
          display: true,
          text: "Date",
          color: isDataQuality ? "#0f172a" : undefined,
        },
      },
      y: {
        beginAtZero: true,
        min: 0 as const,
        grid: isDataQuality
          ? {
              color: "rgba(100, 116, 139, 0.12)",
            }
          : undefined,
        stepSize: isDataQuality ? 1 : null,

        ticks: {
          ...(isDataQuality ? { color: "#334155", stepSize: 1 } : {}),
        },
        title: {
          display: true,
          stepSize: 1,
          color: isDataQuality ? "#0f172a" : undefined,
          text:
            isDataQuality
              ? "Number of failed DQ Tests"
              : "Time (minutes)",
        },
      },
    },
    responsive: true,
    plugins: {
      tooltip: {
        enabled: true,
        intersect: false,
        backgroundColor: isDataQuality
          ? "rgba(15, 23, 42, 0.92)"
          : "rgba(0, 0, 0, 0.7)",
        titleColor: isDataQuality ? "#f8fafc" : "#fff",
        bodyColor: isDataQuality ? "#e2e8f0" : "#fff",
        borderColor: isDataQuality ? "rgba(56, 189, 248, 0.5)" : undefined,
        borderWidth: isDataQuality ? 1 : 0,
        padding: isDataQuality ? 12 : 8,
        callbacks: {
          label: function (context: any) {
            function convertToMinutesAndSeconds(decimalMinutes: any) {
              const totalSeconds = decimalMinutes * 60;
              const minutes = Math.floor(totalSeconds / 60);
              const seconds = Math.floor(totalSeconds % 60);

              const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
              const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;

              return `${formattedMinutes}:${formattedSeconds}`;
            }
            const label = context.dataset.label || "";
            const value = convertToMinutesAndSeconds(context.parsed.y);
            if (type === "dataMonitor") return `${label}: ${value}`;
            else return undefined;
          },
        },
      },
      legend: {
        position: "top" as const,
        labels: {
          usePointStyle: true,
          pointStyle: isDataQuality ? ("circle" as const) : undefined,
          color: isDataQuality ? "#0f172a" : undefined,
          boxWidth: isDataQuality ? 8 : undefined,
        },
      },
      title: {
        display: true,
        text:
          isDataQuality
            ? page === true
              ? "Number of failed DQ-Tests Over time (per subject area)"
              : "Number of failed DQ-Tests Over time (per data mart)"
            : "Job Duration (minutes)",
        font: {
          size: isDataQuality ? 14 : 18,
          weight: "bold" as const,
        },
        color: isDataQuality ? "#0f172a" : undefined,
      },
    },
  };

  const calculateOverallTrendLine = (datasets: any) => {
    const trendLineData = Array.from(
      { length: datasets[0]?.data.length },
      (_, i) => {
        const sum = datasets.reduce(
          (acc: any, dataset: any) => acc + dataset.data[i],
          0
        );
        return sum / datasets.length;
      }
    );
    return trendLineData;
  };

  const trendLineData = chartdata
    ? calculateOverallTrendLine(chartdata?.datasets)
    : null;

  const trendLineChartData = chartdata?.datasets?.length > 1 && {
    label: "Trend Line",
    data: trendLineData,
    borderColor: "rgb(15,23,42)",
    backgroundColor: "rgb(15,23,42)",
    tension: 0.4,
    borderDash: [5],
    pointRadius: 0,
    borderWidth: 2,
  };

  const presentationData =
    chartdata?.datasets?.length > 1
      ? {
          labels: data.labels,
          datasets: [...data.datasets, trendLineChartData],
        }
      : chartdata;

  const styledData =
    isDataQuality && presentationData
      ? {
          ...presentationData,
          datasets: presentationData.datasets.map((dataset: any) => ({
            ...dataset,
            borderWidth: dataset.label === "Trend Line" ? 2 : 2.5,
            tension: dataset.label === "Trend Line" ? 0.35 : 0.28,
            pointRadius: dataset.label === "Trend Line" ? 0 : 3,
            pointHoverRadius: dataset.label === "Trend Line" ? 0 : 5,
          })),
        }
      : presentationData;

  return (
    <Line
      options={options}
      data={styledData}
    />
  );
};

export default LineChart;
